import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Lightbulb, RefreshCw, Trash2, X, ExternalLink, Filter, Rocket, CheckCircle2, Sparkles, Clock } from 'lucide-react';

const STATUS_META = {
  submitted:     { label: 'Submitted',     color: '#6B7280', bg: '#F3F4F6' },
  evaluating:    { label: 'Evaluating',    color: '#BA7517', bg: '#FEF3C7' },
  approved:      { label: 'Approved',      color: '#2D8B5E', bg: '#DCFCE7' },
  in_development:{ label: 'In Development',color: '#3B5BA9', bg: '#EEF2FF' },
  shipped:       { label: 'Shipped',       color: '#2D8B5E', bg: '#DCFCE7' },
  declined:      { label: 'Declined',      color: '#6B7280', bg: '#F3F4F6' },
};
const PRIORITY_META = {
  must_have:    { label: '🔥 Must-have',  color: '#D4726A' },
  should_have:  { label: '⭐ Should-have', color: '#BA7517' },
  nice_to_have: { label: '💡 Nice-to-have', color: '#6B7280' },
};

const fmtDate = (iso) => iso ? new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—';

export default function WishlistInbox() {
  const [data, setData] = useState({ requests: [], counts: { by_status: {}, by_division: {}, by_priority: {}, total: 0 } });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [divFilter, setDivFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (divFilter !== 'all') params.set('division_id', divFilter);
      const r = await api.get(`/wishlist?${params.toString()}`);
      setData(r.data);
    } catch (e) {
      console.error(e);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [statusFilter, divFilter]);

  const changeStatus = async (status) => {
    if (!selected) return;
    setBusy('status');
    try {
      const r = await api.patch(`/wishlist/${selected.id}`, { status, admin_note: selected.admin_note || '' });
      setSelected(r.data);
      await load();
    } catch (e) { alert(e?.response?.data?.detail || 'Update failed'); }
    finally { setBusy(''); }
  };
  const saveNote = async () => {
    if (!selected) return;
    setBusy('note');
    try {
      const r = await api.patch(`/wishlist/${selected.id}`, { status: selected.status, admin_note: selected.admin_note || '' });
      setSelected(r.data);
      await load();
    } catch (e) { alert(e?.response?.data?.detail || 'Update failed'); }
    finally { setBusy(''); }
  };
  const remove = async (id) => {
    if (!window.confirm('Delete this request?')) return;
    try {
      await api.delete(`/wishlist/${id}`);
      setSelected(null);
      await load();
    } catch (e) { alert(e?.response?.data?.detail || 'Delete failed'); }
  };

  const byStatus = data.counts.by_status || {};
  const byDivision = data.counts.by_division || {};
  const filters = [
    { v: 'all', label: 'All', n: data.counts.total || 0 },
    { v: 'submitted', label: 'New', n: byStatus.submitted || 0 },
    { v: 'evaluating', label: 'Evaluating', n: byStatus.evaluating || 0 },
    { v: 'approved', label: 'Approved', n: byStatus.approved || 0 },
    { v: 'in_development', label: 'Building', n: byStatus.in_development || 0 },
    { v: 'shipped', label: 'Shipped', n: byStatus.shipped || 0 },
  ];
  const divisions = Object.keys(byDivision).filter(Boolean).sort();

  return (
    <div className="space-y-4" data-testid="wishlist-inbox">
      <div className="bg-white rounded-xl border p-5" style={{ borderColor: '#E5E1DC' }}>
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <Lightbulb size={18} style={{ color: '#BA7517' }} />
            <div>
              <h2 className="text-sm font-bold" style={{ color: '#1B2A4A' }}>Division Wishlist</h2>
              <p className="text-[11px]" style={{ color: '#6B7280' }}>
                Custom feature requests from division leaders — decide what to build next.
              </p>
            </div>
          </div>
          <button onClick={load} className="px-2.5 py-1.5 rounded-md border text-[11px] flex items-center gap-1" style={{ borderColor: '#E5E1DC', color: '#6B7280' }} data-testid="wishlist-refresh">
            <RefreshCw size={11} /> Refresh
          </button>
        </div>

        {/* Status filter pills */}
        <div className="flex flex-wrap items-center gap-1.5 mb-2">
          <Filter size={12} style={{ color: '#9CA3AF' }} />
          {filters.map(p => {
            const active = statusFilter === p.v;
            return (
              <button key={p.v} onClick={() => setStatusFilter(p.v)}
                className="px-2.5 py-1 rounded-full text-[11px] font-medium border"
                style={{ backgroundColor: active ? '#1B2A4A' : '#fff', color: active ? '#fff' : '#6B7280', borderColor: active ? '#1B2A4A' : '#E5E1DC' }}
                data-testid={`wishlist-filter-${p.v}`}>
                {p.label} <span className="ml-1 opacity-70">{p.n}</span>
              </button>
            );
          })}
        </div>

        {/* Division filter pills */}
        {divisions.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[10px] uppercase tracking-wider" style={{ color: '#9CA3AF' }}>Division:</span>
            <button onClick={() => setDivFilter('all')}
              className="px-2 py-0.5 rounded-full text-[10px] font-medium border"
              style={{ backgroundColor: divFilter === 'all' ? '#2A7B88' : '#fff', color: divFilter === 'all' ? '#fff' : '#6B7280', borderColor: divFilter === 'all' ? '#2A7B88' : '#E5E1DC' }}>
              All
            </button>
            {divisions.map(d => (
              <button key={d} onClick={() => setDivFilter(d)}
                className="px-2 py-0.5 rounded-full text-[10px] font-medium border"
                style={{ backgroundColor: divFilter === d ? '#2A7B88' : '#fff', color: divFilter === d ? '#fff' : '#6B7280', borderColor: divFilter === d ? '#2A7B88' : '#E5E1DC' }}>
                {d} <span className="ml-0.5 opacity-70">{byDivision[d]}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl border" style={{ borderColor: '#E5E1DC' }}>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-5 h-5 border-2 border-[#2A7B88] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : data.requests.length === 0 ? (
          <div className="py-12 text-center text-xs" style={{ color: '#9CA3AF' }}>
            No wishlist items in this filter yet. Leaders can submit on their Quick-Start Guide page.
          </div>
        ) : (
          <div className="divide-y" style={{ borderColor: '#F3F4F6' }}>
            {data.requests.map(r => {
              const sm = STATUS_META[r.status] || STATUS_META.submitted;
              const pm = PRIORITY_META[r.priority] || PRIORITY_META.should_have;
              return (
                <div key={r.id} className="px-4 py-3 hover:bg-gray-50 cursor-pointer" onClick={() => setSelected(r)} data-testid={`wishlist-row-${r.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ backgroundColor: sm.bg, color: sm.color }}>{sm.label}</span>
                        <span className="text-[10px] font-semibold" style={{ color: pm.color }}>{pm.label}</span>
                        {r.division_id && <span className="text-[10px] uppercase tracking-wider" style={{ color: '#6B7280' }}>{r.division_id}</span>}
                        {r.people_impacted > 0 && <span className="text-[10px]" style={{ color: '#6B7280' }}>{r.people_impacted} ppl</span>}
                      </div>
                      <p className="text-sm font-semibold truncate" style={{ color: '#1B2A4A' }}>{r.title}</p>
                      <p className="text-[11px] mt-0.5" style={{ color: '#6B7280' }}>
                        {r.requested_by_name || r.requested_by} · {fmtDate(r.created_at)}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Drawer */}
      {selected && (
        <div className="fixed inset-0 z-50 flex items-start justify-end" style={{ backgroundColor: 'rgba(15,23,42,0.45)' }} onClick={() => setSelected(null)} data-testid="wishlist-drawer">
          <div className="bg-white h-full w-full sm:max-w-xl shadow-2xl overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b px-5 py-3 flex items-center justify-between" style={{ borderColor: '#F3F4F6' }}>
              <p className="text-sm font-bold truncate pr-4" style={{ color: '#1B2A4A' }}>{selected.title}</p>
              <button onClick={() => setSelected(null)} className="p-1 rounded hover:bg-gray-100"><X size={16} style={{ color: '#6B7280' }} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[10px] font-bold px-2 py-0.5 rounded" style={{ backgroundColor: (STATUS_META[selected.status] || STATUS_META.submitted).bg, color: (STATUS_META[selected.status] || STATUS_META.submitted).color }}>
                  {(STATUS_META[selected.status] || STATUS_META.submitted).label}
                </span>
                <span className="text-[10px] font-semibold" style={{ color: (PRIORITY_META[selected.priority] || PRIORITY_META.should_have).color }}>
                  {(PRIORITY_META[selected.priority] || PRIORITY_META.should_have).label}
                </span>
                {selected.division_id && <span className="text-[10px] uppercase tracking-wider" style={{ color: '#6B7280' }}>{selected.division_id}</span>}
              </div>

              <div className="rounded-md p-3 text-[11px] space-y-1" style={{ backgroundColor: '#F9FAFB', color: '#374151' }}>
                <p><b>From:</b> {selected.requested_by_name} ({selected.requested_by}) · {selected.requested_by_role}</p>
                <p><b>Submitted:</b> {fmtDate(selected.created_at)}</p>
                {selected.people_impacted > 0 && <p><b>People impacted:</b> {selected.people_impacted}</p>}
                {selected.expected_benefit && <p><b>Expected benefit:</b> {selected.expected_benefit}</p>}
                {selected.shipped_at && <p><b>Shipped:</b> {fmtDate(selected.shipped_at)}</p>}
              </div>

              {selected.problem && (
                <div>
                  <p className="text-[10px] uppercase tracking-wider font-semibold mb-1" style={{ color: '#9CA3AF' }}>Problem today</p>
                  <p className="text-sm whitespace-pre-wrap" style={{ color: '#1B2A4A' }}>{selected.problem}</p>
                </div>
              )}
              {selected.solution && (
                <div>
                  <p className="text-[10px] uppercase tracking-wider font-semibold mb-1" style={{ color: '#9CA3AF' }}>Proposed solution</p>
                  <p className="text-sm whitespace-pre-wrap" style={{ color: '#1B2A4A' }}>{selected.solution}</p>
                </div>
              )}
              {selected.example_reference && (
                <div>
                  <p className="text-[10px] uppercase tracking-wider font-semibold mb-1" style={{ color: '#9CA3AF' }}>Example / reference</p>
                  <p className="text-sm italic" style={{ color: '#6B7280' }}>{selected.example_reference}</p>
                </div>
              )}

              <div>
                <p className="text-[10px] uppercase tracking-wider font-semibold mb-1.5" style={{ color: '#9CA3AF' }}>Change status</p>
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(STATUS_META).map(([k, v]) => (
                    <button key={k} onClick={() => changeStatus(k)}
                      disabled={busy === 'status' || selected.status === k}
                      className="px-2.5 py-1 rounded text-[11px] font-medium border disabled:opacity-60"
                      style={{ borderColor: selected.status === k ? v.color : '#E5E1DC', backgroundColor: selected.status === k ? v.bg : '#fff', color: selected.status === k ? v.color : '#6B7280' }}
                      data-testid={`wishlist-status-${k}`}>
                      {v.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: '#9CA3AF' }}>Note for the requester</label>
                <textarea value={selected.admin_note || ''} onChange={e => setSelected(s => ({ ...s, admin_note: e.target.value }))}
                  rows={3}
                  placeholder="Visible to the requester — explain decision, ETA, or ask for more info"
                  className="mt-1 w-full text-xs border rounded px-2 py-1.5 resize-none" style={{ borderColor: '#E5E1DC' }}
                  data-testid="wishlist-note" />
                <div className="flex items-center justify-end gap-2 mt-2">
                  <button onClick={() => remove(selected.id)}
                    className="px-2.5 py-1.5 rounded border text-[11px] flex items-center gap-1"
                    style={{ borderColor: '#FECACA', color: '#D4726A' }}>
                    <Trash2 size={11} /> Delete
                  </button>
                  <button onClick={saveNote} disabled={busy === 'note'}
                    className="px-3 py-1.5 rounded text-[11px] font-semibold text-white disabled:opacity-60"
                    style={{ backgroundColor: '#2A7B88' }}
                    data-testid="wishlist-save-note">
                    {busy === 'note' ? 'Saving…' : 'Save note'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
